import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtPHP, fmtDate } from "@/lib/api";
import { PCard, SectionTitle, StatusPill } from "@/components/portal/PortalBits";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Button } from "@/components/ui/button";
import { ArrowLeft, KeyRound, BadgeCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

function OtpBox({ loanId, onDone }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/portal/loans/${loanId}/confirm`, { code });
      toast.success("Loan confirmed");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not verify");
    } finally {
      setBusy(false);
    }
  };
  return (
    <PCard className="border border-blue-200 bg-blue-50/50" data-testid="otp-confirm-card">
      <div className="flex items-center gap-2 font-semibold text-slate-900 text-sm">
        <KeyRound className="h-4 w-4 text-blue-600" /> Enter your verification code
      </div>
      <p className="text-xs text-slate-500 mt-1">Your loan officer has issued a 6-digit code to confirm this loan.</p>
      <div className="flex justify-center my-4">
        <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="otp-input">
          <InputOTPGroup>
            {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="bg-white text-slate-900 h-11 w-11" />)}
          </InputOTPGroup>
        </InputOTP>
      </div>
      <Button onClick={submit} disabled={code.length < 4 || busy} data-testid="otp-submit-button"
        className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
        {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Confirm loan
      </Button>
    </PCard>
  );
}

export default function PortalLoan() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loan, setLoan] = useState(null);
  const load = () => api.get(`/portal/loans/${id}`).then((r) => setLoan(r.data)).catch(() => navigate("/portal"));
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);
  if (!loan) return <div className="text-center text-slate-400 py-20">Loading…</div>;
  return (
    <div data-testid="portal-loan-detail" className="space-y-4">
      <button onClick={() => navigate("/portal")} data-testid="portal-loan-back" className="inline-flex items-center gap-1 text-sm text-slate-500">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <PCard className="bg-[#0F4C81] border-0 text-white">
        <div className="flex items-center justify-between">
          <span className="text-xs text-blue-100">Loan {loan.id.slice(0, 8).toUpperCase()}</span>
          <StatusPill status={loan.status} />
        </div>
        <div className="font-display text-3xl font-bold mt-2" data-testid="portal-loan-principal">{fmtPHP(loan.principal)}</div>
        <div className="grid grid-cols-3 gap-2 mt-4 text-xs">
          <div><div className="text-blue-200">Monthly</div><div className="font-bold">{fmtPHP(loan.monthly_payment)}</div></div>
          <div><div className="text-blue-200">Term</div><div className="font-bold">{loan.term_months} mo</div></div>
          <div><div className="text-blue-200">Balance</div><div className="font-bold" data-testid="portal-loan-balance">{fmtPHP(loan.outstanding_balance)}</div></div>
        </div>
      </PCard>
      {loan.otp_required && <OtpBox loanId={loan.id} onDone={load} />}
      {loan.borrower_confirmed && (
        <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700" data-testid="loan-confirmed-banner">
          <BadgeCheck className="h-4 w-4" /> Confirmed on {fmtDate(loan.confirmed_at)}
        </div>
      )}
      {["Pending", "Under Review"].includes(loan.status) && (
        <PCard className="text-sm text-slate-600" data-testid="loan-pending-note">
          Your application is being reviewed. The admin may adjust the amount or term before approval.
        </PCard>
      )}
      <SectionTitle>Repayment schedule</SectionTitle>
      <PCard className="p-1">
        {loan.installments.map((i) => (
          <div key={i.id} className="flex items-center justify-between p-3 border-b border-slate-100 last:border-0" data-testid={`portal-inst-${i.installment_number}`}>
            <div>
              <div className="text-sm font-semibold text-slate-900">#{i.installment_number} · {fmtDate(i.due_date)}</div>
              <StatusPill status={i.status} />
            </div>
            <div className="text-sm font-bold text-slate-900">{fmtPHP(i.amount_due)}</div>
          </div>
        ))}
      </PCard>
    </div>
  );
}
